import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  pushSupported, getExistingSubscription, subscribeToPush,
  unsubscribeFromPush, sendTestPush,
} from "@/lib/push";
import { BellSimple, BellSlash, CheckCircle, MoonStars, DeviceMobile, PaperPlaneTilt } from "@phosphor-icons/react";

const EVENT_LABELS = {
  new_enquiry: "New enquiry arrives",
  appointment_pending: "Appointment awaiting approval",
  quote_pending: "Quote awaiting approval",
  ai_low_confidence: "AI low-confidence alert",
  calendar_conflict: "Calendar conflict detected",
  customer_reply: "Customer replied",
};

export default function NotificationSettings() {
  const [prefs, setPrefs] = useState(null);
  const [subscribed, setSubscribed] = useState(false);
  const [saving, setSaving] = useState(false);
  const supported = pushSupported();

  const loadPrefs = useCallback(async () => {
    const r = await api.get("/notification-prefs");
    setPrefs(r.data);
  }, []);

  const refreshSubscribed = useCallback(async () => {
    if (!supported) { setSubscribed(false); return; }
    const s = await getExistingSubscription();
    setSubscribed(Boolean(s));
  }, [supported]);

  useEffect(() => {
    loadPrefs();
    refreshSubscribed();
  }, [loadPrefs, refreshSubscribed]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.put("/notification-prefs", patch);
      setPrefs(r.data);
    } finally { setSaving(false); }
  };

  const enablePush = async () => {
    try {
      await subscribeToPush();
      // Auto-set TZ offset for DND (in minutes east of UTC — client convention)
      const tzOffset = -new Date().getTimezoneOffset();
      await save({ push_enabled: true, dnd_tz_offset_min: tzOffset });
      toast.success("Push enabled on this device");
      setSubscribed(true);
    } catch (e) {
      toast.error(e.message || "Could not enable push");
    }
  };

  const disablePush = async () => {
    try {
      await unsubscribeFromPush();
      setSubscribed(false);
      toast("Push disabled on this device");
    } catch (e) {
      toast.error(e.message || "Failed");
    }
  };

  const test = async () => {
    try {
      const r = await sendTestPush();
      toast.success(`Sent — ${r.delivered} device(s)`);
    } catch (e) {
      toast.error("Test failed");
    }
  };

  if (!prefs) return <div className="text-sm text-neutral-500">Loading…</div>;

  const toggleEvent = (key) => {
    save({ events: { ...prefs.events, [key]: !prefs.events?.[key] } });
  };

  return (
    <div>
      <div className="text-xs uppercase tracking-[0.28em] text-neutral-500 dark:text-neutral-500 mb-2">Notifications</div>
      <h2 className="text-2xl font-semibold tracking-tight mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>Mobile push</h2>
      <p className="text-sm text-neutral-500 dark:text-neutral-500 mb-4">
        Install AI Office to your home screen and enable push to get real-time alerts on your phone — even when the tab is closed.
      </p>

      {!supported && (
        <div className="mb-4 p-3 rounded-md bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-xs text-amber-900 dark:text-amber-200">
          This browser doesn&rsquo;t support Web Push. On iPhone: open in Safari → Share → Add to Home Screen → open the icon → enable push here. On Android: works in Chrome/Edge/Firefox.
        </div>
      )}

      {/* Master toggle */}
      <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-neutral-900 p-5 mb-4">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-md flex items-center justify-center bg-[#0F52BA]/10">
            {subscribed
              ? <BellSimple size={22} weight="fill" className="text-[#0F52BA]" />
              : <BellSlash size={22} weight="duotone" className="text-neutral-500" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium flex items-center gap-2">
              This device
              {subscribed && (
                <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded border border-emerald-600 text-emerald-700 flex items-center gap-1">
                  <CheckCircle size={10} weight="fill" /> Subscribed
                </span>
              )}
            </div>
            <div className="text-xs text-neutral-500 dark:text-neutral-500 mt-1 flex items-center gap-1">
              <DeviceMobile size={12} /> Add to Home Screen for the best mobile experience.
            </div>
          </div>
          <div className="flex gap-2">
            {subscribed ? (
              <>
                <button data-testid="push-test-btn" onClick={test} className="text-xs px-3 py-2 rounded-md border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center gap-1">
                  <PaperPlaneTilt size={12} /> Test
                </button>
                <button data-testid="push-disable-btn" onClick={disablePush} className="text-xs px-3 py-2 rounded-md border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800">
                  Disable
                </button>
              </>
            ) : (
              <button data-testid="push-enable-btn" onClick={enablePush} disabled={!supported} className="text-xs px-3 py-2 rounded-md bg-[#0F52BA] text-white hover:bg-[#0C4094] disabled:opacity-50 flex items-center gap-1">
                <BellSimple size={12} weight="fill" /> Enable push
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Event toggles */}
      <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-neutral-900 p-5 mb-4">
        <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500 mb-3">Notify me about</div>
        <div className="space-y-2">
          {Object.entries(EVENT_LABELS).map(([key, label]) => (
            <label key={key} className="flex items-center justify-between text-sm py-2 cursor-pointer">
              <span>{label}</span>
              <input
                type="checkbox"
                data-testid={`notif-event-${key}`}
                checked={Boolean(prefs.events?.[key])}
                onChange={() => toggleEvent(key)}
                disabled={saving}
                className="w-5 h-5 accent-[#0F52BA]"
              />
            </label>
          ))}
        </div>
      </div>

      {/* Do not disturb */}
      <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-neutral-900 p-5">
        <div className="flex items-center gap-3 mb-3">
          <MoonStars size={18} weight="duotone" className="text-[#0F52BA]" />
          <div className="flex-1">
            <div className="text-sm font-medium">Do not disturb</div>
            <div className="text-xs text-neutral-500 dark:text-neutral-500">Mute push notifications during your set hours (your local time).</div>
          </div>
          <label className="inline-flex items-center gap-2">
            <span className="text-xs text-neutral-500 dark:text-neutral-500">{prefs.dnd_enabled ? "On" : "Off"}</span>
            <input
              type="checkbox"
              data-testid="notif-dnd-toggle"
              checked={Boolean(prefs.dnd_enabled)}
              onChange={(e) => {
                const tzOffset = -new Date().getTimezoneOffset();
                save({ dnd_enabled: e.target.checked, dnd_tz_offset_min: tzOffset });
              }}
              className="w-5 h-5 accent-[#0F52BA]"
            />
          </label>
        </div>
        {prefs.dnd_enabled && (
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 dark:text-neutral-500 mb-1">From</div>
              <input
                data-testid="notif-dnd-start"
                type="time"
                value={prefs.dnd_start || "21:00"}
                onChange={(e) => save({ dnd_start: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md text-sm bg-transparent"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 dark:text-neutral-500 mb-1">To</div>
              <input
                data-testid="notif-dnd-end"
                type="time"
                value={prefs.dnd_end || "07:00"}
                onChange={(e) => save({ dnd_end: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md text-sm bg-transparent"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
